import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowUp } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import SiteHeader from "./SiteHeader";
import Footer from "./Footer";

function PageLayout({ children }) {
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const isHome = pathname === "/" || pathname === "/tapes";
  const [showScroll, setShowScroll] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowScroll(window.scrollY > 400);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <SiteHeader />
      <main className="flex-1 px-6 pb-16 pt-10 sm:px-10 lg:px-20">
        {!isHome && (
          <div className="mx-auto mb-10 w-full max-w-3xl">
            <Link
              to="/"
              data-testid="back-to-home-link"
              className="inline-flex items-center gap-2 text-sm transition-colors duration-200 hover:opacity-70"
              style={{
                color: "#1A1A1A",
                fontWeight: 500,
                letterSpacing: "0.3px",
              }}
            >
              <ArrowLeft className="h-4 w-4" />
              <span>{t("backToHome")}</span>
            </Link>
          </div>
        )}
        {children}
      </main>

      {/* Scroll-to-top floating button */}
      {showScroll && (
        <button
          type="button"
          data-testid="scroll-to-top-button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label={t("scrollToTop")}
          className="fixed bottom-6 right-6 z-40 flex h-12 w-12 items-center justify-center rounded-full text-white shadow-lg transition-all duration-200 hover:opacity-90 active:translate-y-[1px]"
          style={{ backgroundColor: "#2A2624" }}
        >
          <ArrowUp className="h-5 w-5" />
        </button>
      )}

      <Footer />
    </div>
  );
}

function Section({ id, eyebrow, heading, children, testId }) {
  return (
    <section id={id || testId} data-testid={testId} className="mx-auto w-full max-w-3xl pt-6 first:pt-0 scroll-mt-24">
      {eyebrow ? (
        <p className="mb-3 text-xs uppercase tracking-[0.2em]" style={{ color: "rgba(26,26,26,0.5)", fontWeight: 500 }}>
          {eyebrow}
        </p>
      ) : null}
      {heading ? (
        <h2 className="text-[32px] leading-[1.15] sm:text-[36px]" style={{ color: "#1A1A1A", fontWeight: 600 }}>
          {heading}
        </h2>
      ) : null}
      <div className={`${heading ? "mt-6" : ""} space-y-5 text-[17px] leading-[1.6] sm:text-[18px]`} style={{ color: "#1A1A1A" }}>
        {children}
      </div>
    </section>
  );
}

function PageTitle({ children, testId }) {
  return (
    <div className="mx-auto w-full max-w-3xl">
      <h1 className="text-[42px] leading-[1.1] sm:text-[48px]" style={{ color: "#1A1A1A", fontWeight: 600 }} data-testid={testId}>
        {children}
      </h1>
    </div>
  );
}

export { PageLayout, Section, PageTitle, useLanguage };
